export interface VocabEntry {
  baseWord: string;
  rankCOCA: string;
  rankBNC: string;
  pos: string;
  irregularPastTense: string;
  irregularPlural: string;
  familyMembers: string;
  chunks: string;
  lextutorBNC: string;
  cefr: string;
  wff: string;
  awl: string;
}

export type PedagogicTier = "receptive" | "productive";

export type TestableAxisField =
  | "irregularPastTense"
  | "irregularPlural"
  | "chunks"
  | "familyMembers"
  | "meaning"
  | "pos";

export interface TestableAxis {
  field: TestableAxisField;
  /** Injected into the LLM prompt */
  directive: string;
  /** Known answer from CSV when applicable; null for open-ended (meaning/flashcard) */
  correctAnswer: string | null;
  /** Guidance for wrong options / distractors */
  distractorHint: string;
}

export type QuestionType = "multiple_choice" | "flashcard" | "fill_blank";

export interface MultipleChoiceQuestion {
  questionType: "multiple_choice";
  axis: TestableAxisField;
  entry: { baseWord: string; pos: string; cefr: string };
  question: string;
  options: string[];
  correctAnswer: string;
  hebrewHint: string;
  hebrewExplanation: string;
}

export interface FlashcardQuestion {
  questionType: "flashcard";
  axis: TestableAxisField;
  entry: { baseWord: string; pos: string; cefr: string };
  front: string;
  back: string;
  hebrewBack: string;
  exampleSentence: string;
}

export interface FillBlankQuestion {
  questionType: "fill_blank";
  axis: TestableAxisField;
  entry: { baseWord: string; pos: string; cefr: string };
  sentence: string;
  correctAnswer: string;
  hint: string;
  hebrewHint: string;
  hebrewExplanation: string;
}

export type QuizQuestion =
  | MultipleChoiceQuestion
  | FlashcardQuestion
  | FillBlankQuestion;

// --- Progressive learning step config ---

export type CefrLevel = "Pre-A1" | "A1" | "A2";
export type GradeGroup = "1-2" | "3-5";

export interface StepConfig {
  step: number;
  cefrLevel: CefrLevel;
  tier: PedagogicTier;
  axes: TestableAxisField[];
  questionTypes: QuestionType[];
}

const ALL_AXES: TestableAxisField[] = [
  "meaning",
  "pos",
  "familyMembers",
  "chunks",
  "irregularPastTense",
  "irregularPlural",
];

/** Grades 1-2: Pre-A1 receptive only, no fill_blank */
export const PROGRESSION_YOUNG: StepConfig[] = [
  { step: 1, cefrLevel: "Pre-A1", tier: "receptive", axes: ["meaning"],                questionTypes: ["flashcard"] },
  { step: 2, cefrLevel: "Pre-A1", tier: "receptive", axes: ["meaning"],                questionTypes: ["flashcard", "multiple_choice"] },
  { step: 3, cefrLevel: "Pre-A1", tier: "receptive", axes: ["meaning", "pos"],         questionTypes: ["flashcard", "multiple_choice"] },
];

/** Grades 3-5: A1→A2 receptive + productive (original progression) */
export const PROGRESSION_ELEMENTARY: StepConfig[] = [
  // A1 Receptive
  { step: 1,  cefrLevel: "A1", tier: "receptive",  axes: ["meaning"],                                questionTypes: ["flashcard"] },
  { step: 2,  cefrLevel: "A1", tier: "receptive",  axes: ["meaning"],                                questionTypes: ["flashcard", "multiple_choice"] },
  { step: 3,  cefrLevel: "A1", tier: "receptive",  axes: ["meaning", "pos"],                         questionTypes: ["flashcard", "multiple_choice"] },
  { step: 4,  cefrLevel: "A1", tier: "receptive",  axes: ["meaning", "pos", "familyMembers"],        questionTypes: ["flashcard", "multiple_choice"] },
  { step: 5,  cefrLevel: "A1", tier: "receptive",  axes: ["meaning", "pos", "familyMembers"],        questionTypes: ["flashcard", "multiple_choice", "fill_blank"] },
  { step: 6,  cefrLevel: "A1", tier: "receptive",  axes: ALL_AXES,                                   questionTypes: ["flashcard", "multiple_choice", "fill_blank"] },
  // A1 Productive
  { step: 7,  cefrLevel: "A1", tier: "productive", axes: ["meaning", "pos", "familyMembers"],        questionTypes: ["multiple_choice", "fill_blank"] },
  { step: 8,  cefrLevel: "A1", tier: "productive", axes: ALL_AXES,                                   questionTypes: ["multiple_choice", "fill_blank"] },
  // A2 Receptive
  { step: 9,  cefrLevel: "A2", tier: "receptive",  axes: ["meaning", "pos"],                         questionTypes: ["flashcard", "multiple_choice"] },
  { step: 10, cefrLevel: "A2", tier: "receptive",  axes: ["meaning", "pos", "familyMembers"],        questionTypes: ["flashcard", "multiple_choice", "fill_blank"] },
  { step: 11, cefrLevel: "A2", tier: "receptive",  axes: ALL_AXES,                                   questionTypes: ["flashcard", "multiple_choice", "fill_blank"] },
  // A2 Productive
  { step: 12, cefrLevel: "A2", tier: "productive", axes: ["meaning", "pos", "familyMembers"],        questionTypes: ["multiple_choice", "fill_blank"] },
  { step: 13, cefrLevel: "A2", tier: "productive", axes: ALL_AXES,                                   questionTypes: ["multiple_choice", "fill_blank"] },
];

/** Backward-compat alias — default to elementary */
export const PROGRESSION = PROGRESSION_ELEMENTARY;

export function getProgression(gradeGroup: GradeGroup): StepConfig[] {
  return gradeGroup === "1-2" ? PROGRESSION_YOUNG : PROGRESSION_ELEMENTARY;
}
